/**
 * Zeitangaben, wie ein Messenger sie zeigt.
 *
 * **Das Sprachgebiet steht in `texte/de.ts`** (`allgemein.gebiet`) und nicht
 * hier: mit der Sprache wechselt auch das Format — aus „5. Okt. 2026" wird
 * „Oct 5, 2026", und die Reihenfolge von Tag und Monat ist keine Kleinigkeit.
 * Die Wörter dieser Datei liegen unter `technik.zeit`.
 */
import { WORTLAUT } from '../texte'
import { uhrText, versatzDesGeraets } from './ortszeit'

const GEBIET = WORTLAUT.allgemein.gebiet

/**
 * Die Formatierer — **und sie werden neu gebaut, wenn das Gerät die Zone
 * wechselt.**
 *
 * Hier standen vier `const`, einmal beim Laden des Moduls gebaut. Das ist die
 * naheliegende Fassung und sie war falsch: **`Intl.DateTimeFormat` bindet die
 * Zeitzone beim Erzeugen.** Wer sie danach am Gerät umstellt, bekommt aus
 * einem bestehenden Formatierer weiter die alte — und die App läuft in einer
 * Web-App vom Home-Bildschirm tage- und wochenlang durch, ohne je neu zu
 * laden.
 *
 * Aus dem Betrieb, mit Bildschirmfoto: das iPhone stand auf New York (06:24 in
 * der Statusleiste), unter der Sprechblase stand **12:20** — Berliner Zeit.
 * Beim Empfänger, der seine Zone nicht angefasst hatte, stimmte alles. Genau
 * dieser Unterschied ist der Fingerabdruck des Fehlers.
 *
 * **Verglichen wird der Versatz und nicht der Zonenname.** `getTimezoneOffset()`
 * ist eine Rechnung ohne Aufbau, ein `Intl.DateTimeFormat()` nur zum Nachsehen
 * wäre genau das, was hier gespart werden soll. Für das Anzeigen einer Uhrzeit
 * reicht er auch: zwei Zonen mit demselben Versatz zeigen dieselbe Zeit. Und
 * er fängt die Sommerzeit mit — ein Gerät, das um drei Uhr nachts umstellt,
 * hat dasselbe Problem im Kleinen.
 */
function bauen(): {
  versatz: number
  time: Intl.DateTimeFormat
  weekday: Intl.DateTimeFormat
  date: Intl.DateTimeFormat
  dateLong: Intl.DateTimeFormat
} {
  return {
    versatz: new Date().getTimezoneOffset(),
    time: new Intl.DateTimeFormat(GEBIET, { hour: '2-digit', minute: '2-digit' }),
    weekday: new Intl.DateTimeFormat(GEBIET, { weekday: 'long' }),
    date: new Intl.DateTimeFormat(GEBIET, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }),
    dateLong: new Intl.DateTimeFormat(GEBIET, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
  }
}

let gebaut = bauen()

/** Die Formatierer von jetzt — neu gebaut, falls die Uhr gewandert ist. */
function uhren(): ReturnType<typeof bauen> {
  if (new Date().getTimezoneOffset() !== gebaut.versatz) gebaut = bauen()
  return gebaut
}

function daysApart(value: Date, now: Date): number {
  const a = new Date(value.getFullYear(), value.getMonth(), value.getDate())
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((b.getTime() - a.getTime()) / 86_400_000)
}

/** Zeitstempel in der Chatliste: heute die Uhrzeit, sonst Tag oder Datum. */
export function listTime(iso: string | null): string {
  if (!iso) return ''
  const value = new Date(iso)
  const apart = daysApart(value, new Date())
  if (apart === 0) return uhren().time.format(value)
  if (apart === 1) return WORTLAUT.technik.zeit.gestern
  if (apart < 7) return uhren().weekday.format(value)
  return uhren().date.format(value)
}

/** Uhrzeit unter einer Sprechblase — in der Uhr des Geräts, das gerade liest. */
export function bubbleTime(iso: string): string {
  return uhren().time.format(new Date(iso))
}

/**
 * Die zweite Uhrzeit: **die des Absenders**, wenn sie abweicht (#71).
 *
 * Aus dem Betrieb, als Entscheidung: *„2 Angaben: jeweils die Lokalzeit des
 * Mitlesenden und des Reisenden (wenn sie abweichend ist)."*
 *
 * Drei Fälle, und nur der dritte ergibt Text:
 *
 *  - **`versatz` ist `null`** — die Nachricht stammt aus der Zeit vor dieser
 *    Spalte. Auf welcher Uhr der Absender stand, weiß niemand mehr, und
 *    geraten wird nicht.
 *  - **Beide Uhren stehen gleich** — dann ist die zweite Zahl dieselbe wie die
 *    erste. Sie danebenzuschreiben wäre kein Hinweis, sondern Lärm.
 *  - **Sie gehen auseinander** — nur dann steht sie da.
 *
 * Gerechnet wird über `uhrText()` in `lib/ortszeit.ts`, dieselbe Stelle wie am
 * Highlight und im Buch: der UTC-Augenblick, gelesen in einer fremden Uhr.
 */
export function absenderZeit(iso: string, versatz: number | null): string {
  if (versatz === null || versatz === versatzDesGeraets()) return ''
  return WORTLAUT.technik.zeit.vorOrt(uhrText(iso, versatz))
}

/**
 * Wie ein Medium heißt, wenn kein Text danebensteht.
 *
 * **Hier, weil es an zwei Stellen gebraucht wird:** im Zitat über der
 * Eingabezeile (dort kennt der Browser das Medium selbst) und im Zitat einer
 * gesendeten Antwort (dort sagt es der Server). An beiden stand fest „Foto".
 * Aus dem Betrieb: „bei Antworten auf Sprachnachrichten steht unten Foto".
 */
export function medienwort(art?: string | null): string {
  const t = WORTLAUT.technik.zeit
  if (art === 'video') return t.video
  if (art === 'audio') return t.sprachnachricht
  return t.foto
}

/** Beschriftung des Datumstrenners im Verlauf. */
export function dayLabel(iso: string): string {
  const value = new Date(iso)
  const apart = daysApart(value, new Date())
  if (apart === 0) return WORTLAUT.technik.zeit.heute
  if (apart === 1) return WORTLAUT.technik.zeit.gestern
  if (apart < 7) return uhren().weekday.format(value)
  return uhren().dateLong.format(value)
}

/** Zwei Nachrichten am selben Kalendertag? */
export function sameDay(a: string, b: string): boolean {
  const x = new Date(a)
  const y = new Date(b)
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  )
}

/**
 * Eine Dateigröße zum Anzeigen.
 *
 * Grob gerundet, und das mit Absicht: „240 MB" beantwortet die Frage, ob das
 * durch die Leitung passt. „239,7 MB" beantwortet sie auch, aber schlechter.
 * Unter einem Megabyte in Kilobyte -- ein verkleinertes Foto liegt dort, und
 * „0,4 MB" liest sich niemand gern.
 *
 * **Ab einem Gigabyte in Gigabyte**, seit es die Speicherkarte gibt: das
 * Kontingent der Anwendung sind 25 GB, und „25600 MB" muss man erst im Kopf
 * teilen, bevor man weiß, ob es viel ist. Eine einzelne Datei kommt dort so
 * gut wie nie hin — die Stelle rechnet sich also fast nur für die Summen,
 * kostet aber nichts.
 */
export function groesse(bytes: number): string {
  // **Nichts ist nicht ein Kilobyte.** Das `Math.max(1, …)` weiter unten ist
  // für eine kleine Datei gedacht — „0,4 kB" liest sich niemand gern —, und
  // für die glatte Null log es: in der Speicherkarte stand „Videos 0 · 1 kB".
  // Aus dem Betrieb am Bildschirmfoto gesehen.
  if (bytes <= 0) return '0 kB'
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
  }
  if (bytes >= 1024 * 1024 * 10) return `${Math.round(bytes / (1024 * 1024))} MB`
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} kB`
}

/** Initialen für den Platzhalter-Avatar. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return WORTLAUT.technik.zeit.ohneNamen
  if (parts.length === 1) return parts[0]!.slice(0, 2)
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`
}

/** Ein Datum zum Anzeigen: „12. September". */
export function dayMonth(iso: string): string {
  return new Date(iso).toLocaleDateString(GEBIET, { day: 'numeric', month: 'long' })
}

/**
 * Der Reisezeitraum in einer Zeile.
 *
 * Wiederholt wird nur, was sich unterscheidet: „12.–18. Juni 2026" im selben
 * Monat, „28. Juni – 3. Juli 2026" im selben Jahr, sonst beide Jahreszahlen.
 * Steht nur eines der beiden Daten fest, sagt das Wort davor, welches.
 *
 * Die Werte kommen als `JJJJ-MM-TT` vom Server, ohne Zeit und ohne Zeitzone.
 * Genau deshalb wird hier von Hand zerlegt: `new Date('2026-06-12')` liest der
 * Browser als Mitternacht UTC und zeigt in westlichen Zeitzonen den Vortag.
 */
export function tripPeriod(von: string | null, bis: string | null): string {
  const t = WORTLAUT.technik.zeit
  if (von === null && bis === null) return ''
  if (von === null) return t.bis(langesDatum(bis!))
  if (bis === null) return t.ab(langesDatum(von))
  if (von === bis) return langesDatum(von)

  const [vj, vm] = von.split('-')
  const [bj, bm] = bis.split('-')

  if (vj !== bj) return t.spanne(langesDatum(von), langesDatum(bis))
  if (vm !== bm) return t.spanne(tagUndMonat(von), langesDatum(bis))
  return t.spanneImMonat(tag(von), langesDatum(bis))
}

function tag(iso: string): string {
  return String(Number(iso.slice(8, 10)))
}

function tagUndMonat(iso: string): string {
  return `${tag(iso)}. ${WORTLAUT.technik.zeit.monate[Number(iso.slice(5, 7)) - 1]}`
}

function langesDatum(iso: string): string {
  return `${tagUndMonat(iso)} ${iso.slice(0, 4)}`
}
